import axiosInstance from "../../../shared/api/axiosInstance";
import { configReq } from "../../../shared/lib/functions";

const base = "admin/v2/Promotion";
const placementCodes = new Set(["home_banner", "home_tabs_top", "category_top", "search_banner", "search_results_top", "category_index_top", "related_ads_top"]);
const config = (token, signal) => ({ ...configReq(token), signal, timeout: 15000 });
const fields = {
  plans: new Set(["name", "priceDisplay", "durationDays", "placementTitles", "enabled", "deleted"]),
  placements: new Set(["code", "title", "description", "enabled", "capacity"]),
};
const endpoint = (kind) => `${base}${kind === "plans" ? "Plans" : "Placements"}`;
const normalize = (value) => {
  if (Array.isArray(value)) return value.map(normalize);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key[0].toLowerCase() + key.slice(1), normalize(item)]));
};
export const normalizePromotionEnvelope = normalize;
export async function savePromotionImage(token, original, file, kind = "plans") {
  const target = `${endpoint(kind)}/${encodeURIComponent(kind === "plans" ? original.id : original.code)}/image`;
  let response;
  if (file) {
    const data = new FormData(); data.set("image", file); data.set("version", String(original.version));
    response = await axiosInstance.post(target, data, { ...configReq(token), timeout: 60000 });
  } else response = await axiosInstance.delete(`${target}?version=${original.version}`, config(token));
  const result = normalize(response.data);
  if (!Number.isSafeInteger(result.version) || (result.imageFileId !== null && (!Number.isSafeInteger(result.imageFileId) || result.imageFileId < 1)))
    throw new Error("invalid promotion image response");
  return result;
}
function headers(value, kind) {
  if (!Array.isArray(value)) throw new Error("promotion-headers");
  const seen = new Set();
  const result = value.map((item) => {
    if (!fields[kind].has(item.name) || seen.has(item.name) || !item.title?.trim()
      || !["string", "status"].includes(item.type)) throw new Error("promotion-headers");
    seen.add(item.name);
    return { name: item.name, title: item.title, type: item.type, showInList: item.showInList === true,
      hasFilter: item.hasFilter === true, filterOrder: Number(item.filterOrder) || 0, colSpan: 1 };
  });
  if (!result.some((item) => item.showInList)) throw new Error("promotion-headers");
  return result;
}
function placement(item) {
  if (!/^[a-z][a-z0-9_]{1,63}$/.test(item.code) || typeof item.title !== "string"
    || typeof item.description !== "string" || typeof item.enabled !== "boolean" || !Number.isSafeInteger(item.version))
    throw new Error("invalid promotion placement");
  return item;
}
function plan(item) {
  if (typeof item.id !== "string" || typeof item.name !== "string" || typeof item.description !== "string"
    || (item.priceMinor !== null && (!Number.isSafeInteger(item.priceMinor) || item.priceMinor < 1)) || item.currency !== "EUR"
    || (item.durationDays !== null && (!Number.isSafeInteger(item.durationDays) || item.durationDays < 1)) || typeof item.enabled !== "boolean"
    || !Number.isSafeInteger(item.version) || !Array.isArray(item.placements)) throw new Error("invalid promotion plan");
  if (item.rialPrice != null && (!Number.isSafeInteger(item.rialPrice) || item.rialPrice < 1 || item.rialPrice > 9999999999)) throw new Error("invalid rial price");
  if (item.enabled && ((item.priceMinor === null && item.rialPrice == null) || item.durationDays === null || !item.placements.length || item.isDeleted))
    throw new Error("invalid active promotion plan");
  return { ...item, isDeleted: item.isDeleted === true, placements: item.placements.map(placement) };
}
export async function getPromotions(token, kind, query, signal) {
  const params = new URLSearchParams({ page: String(query.page), limit: String(query.limit) });
  if (query.search?.trim()) params.set("search", query.search.trim());
  if (["true", "false"].includes(query.enabled)) params.set("enabled", query.enabled);
  if (query.deleted === "true") params.set("deleted", "true");
  const response = await axiosInstance.get(`${endpoint(kind)}?${params}`, config(token, signal));
  const data = normalize(response.data);
  if (!Array.isArray(data.data)) throw new Error("invalid promotion list");
  const rows = kind === "plans" ? data.data.map(plan)
    : data.data.map(placement).filter((item) => placementCodes.has(item.code));
  if (kind === "plans" && (!Number.isSafeInteger(data.total) || data.total < 0)) throw new Error("invalid promotion count");
  return { rows, total: kind === "plans" ? data.total : rows.length, headers: headers(data.header, kind), supportsArchive: data.supportsArchive === true };
}
export async function setPromotionDeleted(token, original, deleted) {
  const target = `${endpoint("plans")}/${encodeURIComponent(original.id)}`;
  const response = deleted ? await axiosInstance.delete(`${target}?version=${original.version}`, config(token))
    : await axiosInstance.post(`${target}/restore`, { version: original.version }, config(token));
  return plan(normalize(response.data));
}
export async function savePromotion(token, kind, original, payload) {
  if (kind !== "plans" && (!original || !placementCodes.has(original.code)))
    throw new Error("Only predefined promotion placements can be configured");
  const identity = original ? kind === "plans" ? original.id : original.code : null;
  const response = identity
    ? await axiosInstance.put(`${endpoint(kind)}/${encodeURIComponent(identity)}`, { ...payload, version: original.version }, config(token))
    : await axiosInstance.post(endpoint(kind), { ...payload, version: 0 }, config(token));
  return (kind === "plans" ? plan : placement)(normalize(response.data));
}
export function promotionError(error) {
  if (error?.message === "promotion-headers") return "ساختار جدول تبلیغات دریافت نشد؛ تنظیمات هدر را بررسی کنید.";
  const status = error?.response?.status;
  if (status === 401 || status === 403) return "برای مدیریت تبلیغات دسترسی مدیر لازم است.";
  if (status === 404) return "سرویس تبلیغات در این محیط آماده نیست؛ نسخه بک‌اند و مهاجرت آن باید نصب شود.";
  if (status === 409) return "اطلاعات هم‌زمان تغییر کرده یا کد تکراری است؛ دوباره بارگذاری کنید.";
  if (status === 400) return "عنوان، قیمت، مدت و جایگاه‌های انتخابی را بررسی کنید.";
  return "دریافت یا ذخیره اطلاعات تبلیغات انجام نشد؛ دوباره تلاش کنید.";
}
